/**
 * Global frontend error reporting.
 *
 * Installs window-level listeners so EVERY uncaught error and unhandled promise
 * rejection in the app is forwarded to the backend (`logClientError` →
 * `POST /api/v1/client/error` → `logger.error` → Telegram private chat).
 *
 * This is the catch-all net for errors that don't surface through a specific
 * try/catch (e.g. the Solana `Can't find variable: Buffer` adoption crash, which
 * died in a wallet chunk before any backend call and so was never alerted on).
 * Render errors are additionally caught by `ErrorBoundary`, which reports the same way.
 *
 * Must never throw: reporting failures are swallowed so we never turn a reportable
 * error into a second crash (or a reporting loop).
 */
import { logClientError, logClientWarning } from '../api/clientLogApi';
import { classifyClientIssue } from './clientIssueClassifier';

let installed = false;

/** Build a short context string (page + stack) for a reported error. Never throws. */
function buildContext(stack?: string): string {
  try {
    const page = typeof window !== 'undefined' ? window.location.href : 'n/a';
    return stack ? `page=${page}\n${stack}` : `page=${page}`;
  } catch {
    return stack ?? 'n/a';
  }
}

/** Extract a human-readable message from any thrown/rejected value. */
function messageOf(value: unknown): string {
  if (value instanceof Error) return value.message || value.name;
  if (typeof value === 'string') return value;
  if (value == null) return '(no error value)';
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function reportGlobalIssue(prefix: string, message: string, stack?: string): void {
  const context = buildContext(stack);
  const report = `${prefix} ${message}`;
  const disposition = classifyClientIssue(message, context);
  if (disposition === 'ignore') return;
  if (disposition === 'warn') {
    logClientWarning(report, context);
    return;
  }
  logClientError(report, context);
}

// ---------------------------------------------------------------------------
// Error-toast reporting
// ---------------------------------------------------------------------------

/**
 * How long the same toast message stays deduped. A user who retries a failing action four
 * times in a minute (as happened with the transfer-suika 403) should produce ONE alert,
 * not four — the repeat carries no new information.
 */
export const TOAST_REPORT_DEDUPE_MS = 60_000;

const recentToastReports = new Map<string, number>();

/** Test seam: clear the dedupe window between cases. Not used by app code. */
export function __resetToastReportDedupe(): void {
  recentToastReports.clear();
}

/**
 * Report a user-visible ERROR toast to the ops channels.
 *
 * Error toasts were previously invisible: `showToast(msg, 'error')` renders a failure the
 * player sees, but a handled 4xx never throws and never hits `console.error`, so neither the
 * global window listeners nor PostHog's console capture saw it. The only trace of a real
 * denial was the backend's own truncated `warn` request line — which is why "You do not own
 * this pet" could not be found in the logs at error level.
 *
 * Routed through the SAME {@link classifyClientIssue} net as the other two channels, so
 * expected gameplay validation ("too tired", "insufficient balance", "name already taken")
 * stays silent and only genuine failures page us. Never throws.
 */
export function reportErrorToast(message: string): void {
  try {
    if (!message) return;

    const now = Date.now();
    const lastReportedAt = recentToastReports.get(message);
    if (lastReportedAt !== undefined && now - lastReportedAt < TOAST_REPORT_DEDUPE_MS) return;

    // Prune stale entries so a long session can't grow the map without bound.
    for (const [seen, at] of recentToastReports) {
      if (now - at >= TOAST_REPORT_DEDUPE_MS) recentToastReports.delete(seen);
    }

    const context = buildContext();
    const disposition = classifyClientIssue(message, context);
    if (disposition === 'ignore') return;

    recentToastReports.set(message, now);
    const report = `[Toast] ${message}`;
    if (disposition === 'warn') {
      logClientWarning(report, context);
      return;
    }
    logClientError(report, context);
  } catch {
    // Reporting must never turn a failed action into a second failure.
  }
}

function handleError(event: ErrorEvent): void {
  try {
    // Resource-load failures (img/script/link) surface as ErrorEvents with no `error`
    // and no `message`; they're noise for this channel, so skip them.
    if (!event.error && !event.message) return;
    const err = event.error;
    const message = event.message || messageOf(err);
    const stack = err instanceof Error ? err.stack : undefined;
    reportGlobalIssue('[Uncaught]', message, stack);
  } catch {
    /* never let reporting throw */
  }
}

function handleRejection(event: PromiseRejectionEvent): void {
  try {
    const reason = event.reason;
    const stack = reason instanceof Error ? reason.stack : undefined;
    reportGlobalIssue('[UnhandledRejection]', messageOf(reason), stack);
  } catch {
    /* never let reporting throw */
  }
}

/**
 * Install the global error/rejection listeners. Idempotent — safe to call once at
 * app startup (from `main.tsx`, after polyfills). No-op outside the browser.
 */
export function installGlobalErrorReporting(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', handleError);
  window.addEventListener('unhandledrejection', handleRejection);
}
